import Vue from 'vue';
import VueRouter, { Route, RouteConfig } from 'vue-router';
import store from '@/store';
import { AuthAction } from '@/store/AuthModule';
import { PreferencesAction } from '@/store/PreferencesModule';
import { Dictionary, NavigationGuardNext } from 'vue-router/types/router';

Vue.use(VueRouter);
const ifNotAuthenticated = (to: Route, from: Route, next: NavigationGuardNext) => {
  if (!store.getters['AuthModule/isAuthenticated']) {
    next();
    return;
  }
  const params: Dictionary<string> = {};
  if (to.query.source) {
    params.source = to.query.source as string;
  }
  next({ name: 'start', params });
};

const ifAuthenticated = (to: Route, from: Route, next: NavigationGuardNext) => {
  if (store.getters['AuthModule/isAuthenticated']) {
    next();
    return;
  }
  next({ name: 'login' });
};

const ifSuperAdmin = (to: Route, from: Route, next: NavigationGuardNext) => {
  if (store.getters['AuthModule/isSuperAdmin']) {
    next();
    return;
  }
  next({ name: 'login' });
};

const routes: Array<RouteConfig> = [
  {
    path: '/',
    name: 'login',
    component: () =>
      import(
        /* webpackPrefetch: true */ /* webpackChunkName: "login" */ '@/views/pages/auth/Login.vue'
      ),
    meta: {
      rule: 'editor',
      layout: 'layout-guest',
      title: 'Login',
    },
    beforeEnter: ifNotAuthenticated,
  },
  {
    path: '/bestaetigen/:code',
    name: 'confirm-account',
    component: () => import(/* webpackChunkName: "auth" */ '@/views/pages/auth/ConfirmAccount.vue'),
    meta: {
      rule: 'editor',
      layout: 'layout-guest',
      title: 'Account bestätigen',
    },
  },
  {
    path: '/passwort-vergessen',
    name: 'forgot-password',
    component: () => import(/* webpackChunkName: "auth" */ '@/views/pages/auth/ForgotPassword.vue'),
    meta: {
      rule: 'editor',
      layout: 'layout-guest',
      title: 'Passwort vergessen',
    },
    beforeEnter: ifNotAuthenticated,
  },
  {
    path: '/passwort-zuruecksetzen/:id',
    name: 'reset-password',
    component: () => import(/* webpackChunkName: "auth" */ '@/views/pages/auth/ResetPassword.vue'),
    meta: {
      rule: 'editor',
      layout: 'layout-guest',
      title: 'Passwort zurücksetzen',
    },
    beforeEnter: ifNotAuthenticated,
  },
  {
    path: '/logout',
    name: 'logout',
    component: () => import(/* webpackChunkName: "login" */ '@/views/pages/auth/Login.vue'),
    meta: {
      rule: 'editor',
      layout: 'layout-guest',
      title: 'Logout',
    },
    beforeEnter: ifNotAuthenticated,
  },
  {
    path: '/impressum',
    name: 'imprint',
    component: () => import(/* webpackChunkName: "auth" */ '@/views/pages/auth/Imprint.vue'),
    meta: {
      rule: 'editor',
      layout: 'layout-guest',
      title: 'Impressum',
    },
    // beforeEnter: ifNotAuthenticated,
  },
  {
    path: '/datenschutz',
    name: 'privacy',
    component: () => import(/* webpackChunkName: "auth" */ '@/views/pages/auth/PrivacyPolicy.vue'),
    meta: {
      rule: 'editor',
      layout: 'layout-guest',
      title: 'Datenschutzerklärung',
    },
    // beforeEnter: ifNotAuthenticated,
  },
  // {
  //   path: '/',
  //   name: 'empty',
  //   component: () => import('@/views/pages/Empty.vue'),
  //   meta: {
  //     rule: 'editor',
  //   },
  // },
  {
    path: '/start',
    name: 'start',
    component: () => import(/* webpackChunkName: "start" */ '@/views/pages/Start.vue'),
    meta: {
      rule: 'editor',
      layout: 'layout-main',
      title: 'Start',
      sensitiveData: true,
    },
    beforeEnter: ifAuthenticated,
  },

  {
    path: '/vertraege',
    name: 'policies.all',
    component: () =>
      import(
        /* webpackPrefetch: true */ /* webpackChunkName: "all-policies" */ '@/views/pages/policies/AllPolicies.vue'
      ),
    meta: {
      rule: 'editor',
      layout: 'layout-main',
      filter: 'policy-filter',
      title: 'Anbahnungen',
      sensitiveData: true,
    },
    beforeEnter: ifAuthenticated,
  },

  {
    path: '/vertraege/:id',
    name: 'policies.edit',
    component: () =>
      import(/* webpackChunkName: "all-policies" */ '@/views/pages/policies/AllPolicies.vue'),
    meta: {
      rule: 'editor',
      layout: 'layout-main',
      filter: 'policy-filter',
      title: 'Anbahnungen',
      sensitiveData: true,
    },
    beforeEnter: ifAuthenticated,
  },

  {
    path: '/anbahnungen',
    name: 'policies.all',
    component: () =>
      import(
        /* webpackPrefetch: true */ /* webpackChunkName: "all-policies" */ '@/views/pages/policies/AllPolicies.vue'
      ),
    meta: {
      rule: 'editor',
      layout: 'layout-main',
      filter: 'policy-filter',
      title: 'Anbahnungen',
      sensitiveData: true,
    },
    beforeEnter: ifAuthenticated,
  },

  {
    path: '/antragsbuch',
    name: 'antragsbuch',
    component: () =>
      import(
        /* webpackPrefetch: true */ /* webpackChunkName: "antragsbuch" */ '@/views/pages/antragsbuch/Antragsbuch.vue'
      ),
    meta: {
      rule: 'editor',
      layout: 'layout-main',
      filter: 'antragsbuch-filter',
      title: 'Antragsbuch',
      sensitiveData: true,
    },
    beforeEnter: ifAuthenticated,
  },

  {
    path: '/statistik',
    name: 'statistics',
    component: () =>
      import(
        /* webpackPrefetch: true */ /* webpackChunkName: "antragsbuch" */ '@/views/pages/statistics/Statistics.vue'
      ),
    meta: {
      rule: 'editor',
      layout: 'layout-main',
      filter: 'statistic-filter',
      title: 'Statistik',
      //sensitiveData: true,
    },
    beforeEnter: ifAuthenticated,
  },

  {
    path: '/vertraege/:id',
    name: 'policy.update',
    component: () =>
      import(/* webpackChunkName: "all-policies" */ '@/views/pages/policies/AllPolicies.vue'),
    meta: {
      rule: 'editor',
      layout: 'layout-main',
      filter: 'policy-filter',
      title: 'Anbahnungen',
      sensitiveData: true,
    },
    beforeEnter: ifAuthenticated,
  },

  {
    path: '/admin/agenturen',
    name: 'admin.agencies',
    component: () => import(/* webpackChunkName: "agencies" */ '@/views/pages/admin/Agencies.vue'),
    meta: {
      rule: 'editor',
      layout: 'layout-main',
      title: 'Agenturen',
    },

    beforeEnter: ifSuperAdmin,
  },
  {
    path: '/admin/announcements',
    name: 'admin.announcements',
    component: () =>
      import(/* webpackChunkName: "agencies" */ '@/views/pages/admin/Announcements.vue'),
    meta: {
      rule: 'editor',
      layout: 'layout-main',
      title: 'Announcements',
    },

    beforeEnter: ifSuperAdmin,
  },
  {
    path: '/admin/feedback',
    name: 'admin.feedback',
    component: () => import(/* webpackChunkName: "feedback" */ '@/views/pages/admin/Feedback.vue'),
    meta: {
      rule: 'editor',
      layout: 'layout-main',
      title: 'Feedback',
    },
    beforeEnter: ifSuperAdmin,
  },
  {
    path: '/aufgaben',
    name: 'tasks2',
    component: () => import(/* webpackChunkName: "tasks2" */ '@/views/pages/tasks2/Tasks.vue'),
    meta: {
      rule: 'editor',
      layout: 'layout-main',
      title: 'Aufgaben',
      sensitiveData: true,
    },
    beforeEnter: ifAuthenticated,
  },
  {
    path: '/aufgaben/:id',
    name: 'tasks2.edit',
    component: () => import(/* webpackChunkName: "tasks2" */ '@/views/pages/tasks2/Tasks.vue'),
    meta: {
      rule: 'editor',
      layout: 'layout-main',
      title: 'Aufgaben',
      sensitiveData: true,
    },
    beforeEnter: ifAuthenticated,
  },
  {
    path: '/tasks',
    name: 'tasks',
    component: () => import(/* webpackChunkName: "tasks" */ '@/views/pages/tasks/Tasks.vue'),
    meta: {
      rule: 'editor',
      layout: 'layout-main',
      filter: 'task-filter',
      title: 'Aufgaben',
      sensitiveData: true,
    },
    beforeEnter: ifAuthenticated,
  },
  {
    path: '/tasks/:id',
    name: 'tasks.edit',
    component: () => import(/* webpackChunkName: "tasks" */ '@/views/pages/tasks/Tasks.vue'),
    meta: {
      rule: 'editor',
      layout: 'layout-main',
      filter: 'task-filter',
      title: 'Aufgaben',
      sensitiveData: true,
    },
    beforeEnter: ifAuthenticated,
  },
  {
    path: '/kunden',
    name: 'customers',
    component: () =>
      import(/* webpackChunkName: "customers" */ '@/views/pages/customers/Customers.vue'),
    meta: {
      rule: 'editor',
      layout: 'layout-main',
      title: 'Kunden',
      sensitiveData: true,
    },
    beforeEnter: ifAuthenticated,
  },
  {
    path: '/kunden/:id',
    name: 'customers.detail',
    component: () =>
      import(/* webpackChunkName: "customers" */ '@/views/pages/customers/CustomerDetail.vue'),
    meta: {
      rule: 'editor',
      layout: 'layout-main',
      title: 'Kunde',
      sensitiveData: true,
    },
    beforeEnter: ifAuthenticated,
  },
  {
    path: '/schaeden',
    name: 'damages',
    component: () => import(/* webpackChunkName: "damages" */ '@/views/pages/damages/Damages.vue'),
    meta: {
      rule: 'editor',
      layout: 'layout-main',
      filter: 'damage-filter',
      title: 'Schäden',
      sensitiveData: true,
    },
    beforeEnter: ifAuthenticated,
  },
  {
    path: '/schaeden/:id',
    name: 'damages.edit',
    component: () => import(/* webpackChunkName: "damages" */ '@/views/pages/damages/Damages.vue'),
    meta: {
      rule: 'editor',
      layout: 'layout-main',
      title: 'Schäden',
      sensitiveData: true,
    },
    beforeEnter: ifAuthenticated,
  },
  {
    path: '/ziele/geschaeftsplan',
    name: 'agency.goals.businessplan',
    component: () =>
      import(/* webpackChunkName: "fivgoals" */ '@/views/pages/fivgoals/BusinessPlan.vue'),
    meta: {
      rule: 'editor',
      layout: 'layout-main',
      title: 'Geschäftsplan',
      filter: 'fiv-goals-filter',
    },
    beforeEnter: ifAuthenticated,
  },

  {
    path: '/ziele/team',
    name: 'agency.goals.team',
    component: () => import(/* webpackChunkName: "goals" */ '@/views/pages/goals/Goals.vue'),
    meta: {
      rule: 'editor',
      layout: 'layout-main',
      title: 'Team-Ziele',
      actionButton: 'Team-Ziel anlegen',
      type: 'team',
      filter: 'fiv-goals-filter',
      sensitiveData: false,
    },
    beforeEnter: ifAuthenticated,
  },
  {
    path: '/ziele/agentur',
    name: 'agency.goals.agency',
    component: () => import(/* webpackChunkName: "goals" */ '@/views/pages/goals/Goals.vue'),
    meta: {
      rule: 'editor',
      layout: 'layout-main',
      title: 'Agentur-Ziele',
      actionButton: 'Agentur-Ziel anlegen',
      type: 'agency',
      filter: 'fiv-goals-filter',
      sensitiveData: false,
    },
    beforeEnter: ifAuthenticated,
  },
  {
    path: '/ziele/mitarbeiter',
    name: 'agency.goals.users',
    component: () => import(/* webpackChunkName: "goals" */ '@/views/pages/goals/Goals.vue'),
    meta: {
      rule: 'editor',
      layout: 'layout-main',
      title: 'Mitarbeiter-Ziele',
      actionButton: 'Mitarbeiter-Ziel anlegen',
      type: 'user',
      filter: 'fiv-goals-filter',
      sensitiveData: false,
    },
    beforeEnter: ifAuthenticated,
  },
  {
    path: '/ziele',
    name: 'my-goals',
    component: () => import(/* webpackChunkName: "goals" */ '@/views/pages/goals/Goals.vue'),
    meta: {
      rule: 'editor',
      layout: 'layout-main',
      title: 'Meine Ziele',
      actionButton: 'Ziel anlegen',
      type: 'user',
      filter: 'fiv-goals-filter',
      sensitiveData: false,
    },
    beforeEnter: ifAuthenticated,
  },
  {
    path: '/einstellungen',
    name: 'settings',
    component: () =>
      import(/* webpackChunkName: "settings" */ '@/views/pages/settings/Settings.vue'),
    meta: {
      rule: 'editor',
      layout: 'layout-main',
      title: 'Einstellungen',
    },
    beforeEnter: ifAuthenticated,
  },
  {
    path: '/hilfe',
    name: 'help',
    component: () => import(/* webpackChunkName: "help" */ '@/views/pages/help/Help.vue'),
    meta: {
      rule: 'editor',
      layout: 'layout-main',
      title: 'Hilfe',
    },
    beforeEnter: ifAuthenticated,
  },
  {
    path: '/dokumentenablage',
    name: 'file-inbox',
    component: () =>
      import(/* webpackChunkName: "file-inbox" */ '@/views/pages/file-inbox/FileInbox.vue'),
    meta: {
      rule: 'editor',
      layout: 'layout-main',
      title: 'Dokumentenablage',
    },
    beforeEnter: ifAuthenticated,
  },
  {
    path: '*',
    name: 'notfound',
    component: () => import('@/views/pages/NotFound.vue'),
    meta: {
      rule: 'editor',
      layout: 'layout-guest',
      title: 'Seite nicht gefunden',
    },
  },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
});
router.beforeEach(async (to, from, next) => {
  document.title = to.meta.title + ' - Admiral Dashboard';
  await store.dispatch(`AuthModule/${AuthAction.UpdateLastApiCall}`);
  await store.dispatch(`PreferencesModule/${PreferencesAction.expandMobileMenu}`, false);

  next();
});

router.afterEach((to, from) => {
  if (window.app) {
    window.app.$root.$emit('route-changed', to.name);
  }
  if ((window as any).op) {
    // window.op('track', 'screen_view');
  }
});

export default router;
